import React from 'react';
import classNames from 'classnames';

import useDeviceDetect from 'Hooks/useDeviceDetect';
import { CrudHeader, CrudPageLayout } from 'Modules/Crud';
import { BreadcrumbItem } from 'Views/Components/New/Breadcrumbs';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import If from 'Views/Components/If/If';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { EditActions, MobileSaveBar } from './EditActions';
import SectionTabs, { SectionTab } from './SectionTabs';
import { TabEditSessionProvider, useTabEditSession } from './TabEditSession';

export interface AccountPageShellProps {
	className: string;
	title: React.ReactNode;
	subtitle: string;
	breadcrumbs?: BreadcrumbItem[];
	mode: EntityFormMode;
	saving: boolean;
	onSave: () => void;
	onCancel: () => void;
	/** Verification banners etc., rendered above the account card at its width. */
	banners?: React.ReactNode;
	tabs: SectionTab[];
	activeTab: string;
	onTabChange: (tabId: string) => void;
	/** Tab ids that stay switchable during an edit session; the rest lock until save/cancel. */
	editSessionTabs: string[];
	/**
	 * Where the horizontal page padding sits relative to the account card's 1000px cap.
	 * 'outside' (staff): padding on a wide outer container, so the card keeps its full 1000px
	 * on desktop. 'inside' (customer, default): padding within the cap — the legacy customer
	 * card width. Preserved per page so neither shifts.
	 */
	pagePadding?: 'outside' | 'inside';
	/** The active tab's body, rendered inside the account card. */
	children: React.ReactNode;
	/**
	 * Anything between the banners and the account card. The staff page puts its activity card here on
	 * small screens, where below the account card it sat a long scroll away.
	 */
	leadingContent?: React.ReactNode;
	/** Anything below the account card (the staff page's activity card and notes modal). */
	extraContent?: React.ReactNode;
}

/**
 * The account pages' common chrome: Crud layout + sticky header (with the edit session's
 * Save/Cancel on desktop), the banner slot, the form-width account card with its tab row, and
 * the mobile save bar. UserDetailInner and CustomerAccountInner stay thin pages on top of this —
 * tab definitions, save flows and staff extras only.
 */
export default function AccountPageShell(props: AccountPageShellProps) {
	// The provider has to sit above the shell's own render, since the header reads what the tab body
	// below it registers.
	return (
		<TabEditSessionProvider>
			<AccountPageShellInner {...props} />
		</TabEditSessionProvider>
	);
}

function AccountPageShellInner({
	className,
	title,
	subtitle,
	breadcrumbs,
	mode,
	saving,
	onSave,
	onCancel,
	banners,
	tabs,
	activeTab,
	onTabChange,
	editSessionTabs,
	pagePadding = 'inside',
	children,
	leadingContent,
	extraContent,
}: AccountPageShellProps) {
	const { isMobile } = useDeviceDetect();

	// A tab that stages its own edits — communication preferences — publishes its Save/Discard here so
	// they land in the same place as every other Save on these pages. The page's own edit mode wins if
	// somehow both are live: that one locks the tab row, so it is the session the user is actually in.
	const tabSession = useTabEditSession();
	const pageEditing = mode !== EntityFormMode.VIEW;
	const editing = pageEditing || tabSession != null;

	const actionProps = pageEditing
		? { saving, onSave, onCancel }
		: {
				saving: tabSession?.saving ?? false,
				onSave: tabSession?.onSave ?? (() => {}),
				onCancel: tabSession?.onCancel ?? (() => {}),
				saveLabel: tabSession?.saveLabel,
				savingLabel: tabSession?.savingLabel,
				cancelLabel: tabSession?.cancelLabel,
			};

	// Desktop keeps Save/Cancel in the sticky header; on mobile they'd crowd the identity card,
	// so they move to a thumb-reachable bar fixed to the bottom of the viewport instead.
	const headerActions = editing && !isMobile ? <EditActions {...actionProps} /> : null;

	/**
	 * Leaving a tab that has staged edits.
	 *
	 * The page's own edit mode does not need this — it locks the tab row to `editSessionTabs`, so
	 * there is nowhere to go. A tab session leaves every tab reachable, and switching away unmounts
	 * the tab body and takes its staged changes with it. Silently, which is the part worth a modal:
	 * the customer sees the Save disappear from the header and has no way to know what they lost.
	 */
	const changeTab = (tabId: string) => {
		if (tabSession == null || tabId === activeTab) {
			onTabChange(tabId);
			return;
		}

		newConfirmModal(
			'You have unsaved changes',
			<div>Leaving this tab will discard them.</div>,
			{ confirmText: 'Discard changes', cancelText: 'Stay here', resolveOnCancel: true },
		)
			.then(confirmed => {
				if (confirmed) {
					// Cleared before the switch so the tab body unmounts with nothing staged, and its
					// Save does not flash in the header on the way out.
					tabSession.onCancel();
					onTabChange(tabId);
				}
			})
			// resolveOnCancel is on, so a rejection here is the modal being dismissed rather than a
			// choice. Either way, staying put is the safe outcome.
			.catch(() => undefined);
	};

	const columnClassName = classNames(
		'mx-auto w-full px-4 md:px-8',
		pagePadding === 'outside' ? 'max-w-[1400px]' : 'max-w-[1000px]',
	);
	const innerColumnClassName = classNames(pagePadding === 'outside' && 'mx-auto w-full max-w-[1000px]');

	return (
		<CrudPageLayout className={className}>
			<CrudHeader title={title} subtitle={subtitle} breadcrumbs={breadcrumbs} actions={headerActions} />

			{/*
				Account card — form-width so the detail fields don't stretch stupidly wide. The banners sit
				in a column of the same width, kept apart from the card so leading content can go between
				them without pushing a warning below the fold.
			*/}
			<div className={columnClassName}>
				<div className={innerColumnClassName}>{banners}</div>
			</div>

			{leadingContent}

			<div className={columnClassName}>
				<div className={innerColumnClassName}>
					<SectionTabs
						tabs={tabs}
						activeTab={activeTab}
						onChange={changeTab}
						isMobile={isMobile}
						lockedToIds={mode === EntityFormMode.EDIT ? editSessionTabs : undefined}
					/>
					<div
						className={classNames(
							'account-page border border-gray-200 bg-white p-6 md:p-8',
							isMobile ? 'mt-3 rounded-xl' : 'rounded-b-xl border-t-0',
						)}
					>
						{children}
					</div>
				</div>
			</div>

			{extraContent}

			<If condition={editing && isMobile}>
				<MobileSaveBar {...actionProps} />
			</If>
		</CrudPageLayout>
	);
}
